import { useListingField, useListingStore } from '@brightmls/nextgen.lae.flc';
import { ListingResource, PropertyField } from '../../../system/field';
import {
  formatAddressLineOne,
  formatAddressLineTwo,
} from '../../../system/location';

// ---------------------------------------------------------------------------
// Utility: batch-read a set of fields from the FLC store, filter out empties
// ---------------------------------------------------------------------------

type FieldDef = { key: string; label: string };
export type FieldEntry = { label: string; value: string };

/**
 * Read multiple listing fields at once and return only those with non-empty values.
 * Each section hook declares its field list, and this does the rest.
 */
export function useFieldSection(defs: FieldDef[]): FieldEntry[] {
  return useListingStore((state) => {
    return defs
      .map((d) => ({
        label: d.label,
        value: String(state.Property?.[d.key] ?? ''),
      }))
      .filter(
        (f) => f.value !== '' && f.value !== 'undefined' && f.value !== 'null',
      );
  });
}

// ---------------------------------------------------------------------------
// Header / highlights hooks
// ---------------------------------------------------------------------------

/** Modification timestamp for "Last saved on" */
export function usePreviewHeader() {
  const modTimestamp = useListingField(
    ListingResource.Property,
    PropertyField.MODIFICATION_TIMESTAMP,
    '',
  ).value;

  return { modificationTimestamp: modTimestamp };
}

/** Agent contact info for the orange bar */
export function usePreviewAgent() {
  const agentName = useListingField(
    ListingResource.Property,
    PropertyField.LIST_AGENT_FULL_NAME,
    '',
  ).value;
  const officeName = useListingField(
    ListingResource.Property,
    PropertyField.LIST_OFFICE_NAME,
    '',
  ).value;
  const agentPhone = useListingField(
    ListingResource.Property,
    PropertyField.LIST_AGENT_DIRECT_PHONE,
    '',
  ).value;

  return { agentName, officeName, agentPhone };
}

/** Core highlights: address, price, beds, baths, sqft, status, type, MLS# */
export function usePreviewHighlights() {
  const f = (field: string) =>
    useListingField(ListingResource.Property, field, '').value;

  const streetNumber = f(PropertyField.STREET_NUMBER);
  const streetDirPrefix = f(PropertyField.STREET_DIR_PREFIX);
  const streetName = f(PropertyField.STREET_NAME);
  const streetSuffix = f(PropertyField.STREET_SUFFIX);
  const streetDirSuffix = f(PropertyField.STREET_DIR_SUFFIX);
  const unitNumber = f(PropertyField.UNIT_NUMBER);
  const city = f(PropertyField.CITY);
  const state = f(PropertyField.STATE_OR_PROVINCE);
  const zipCode = f(PropertyField.POSTAL_CODE);
  const zipCodePlus4 = f(PropertyField.POSTAL_CODE_PLUS4);

  const listPrice = f(PropertyField.LIST_PRICE);
  const beds = f(PropertyField.BEDROOMS_TOTAL);
  const baths = f(PropertyField.BATHROOMS_FULL);
  const halfBaths = f(PropertyField.BATHROOMS_HALF);
  const sqft = f(PropertyField.LIVING_AREA);
  const aboveGrade = f(PropertyField.ABOVE_GRADE_FINISHED_AREA);
  const propertyType = f(PropertyField.PROPERTY_TYPE);
  const mlsStatus = f(PropertyField.MLS_STATUS);
  const listingId = f(PropertyField.LISTING_ID);

  const addressLine1 = formatAddressLineOne({
    streetNumber,
    streetDirPrefix,
    streetName,
    streetSuffix,
    streetDirSuffix,
    unitNumber,
  });

  const addressLine2 = formatAddressLineTwo({
    city,
    state,
    zipCode,
    zipCodePlus4,
  });

  return {
    addressLine1,
    addressLine2,
    listPrice,
    beds,
    baths,
    halfBaths,
    sqft,
    aboveGrade,
    propertyType,
    mlsStatus,
    listingId,
  };
}

/** Lat/lng for the HERE map */
export function usePreviewMap() {
  const lat = useListingField(
    ListingResource.Property,
    PropertyField.LATITUDE,
    '',
  ).value;
  const lng = useListingField(
    ListingResource.Property,
    PropertyField.LONGITUDE,
    '',
  ).value;

  return {
    latitude: parseFloat(lat) || 0,
    longitude: parseFloat(lng) || 0,
    hasCoordinates: !!lat && !!lng,
  };
}

// ---------------------------------------------------------------------------
// Field section hooks — each returns FieldEntry[] (auto-filtered)
// ---------------------------------------------------------------------------

export const useInternetSettings = () =>
  useFieldSection([
    {
      key: PropertyField.INTERNET_ENTIRE_LISTING_DISPLAY_YN,
      label: 'Seller Authorizes the listing on the Internet',
    },
    {
      key: PropertyField.INTERNET_ADDRESS_DISPLAY_YN,
      label: 'Seller Authorizes Address on the Internet',
    },
    {
      key: PropertyField.INTERNET_AUTOMATED_VALUATION_DISPLAY_YN,
      label: 'Seller Authorizes AVM',
    },
    {
      key: PropertyField.INTERNET_CONSUMER_COMMENT_YN,
      label: 'Seller Authorizes Consumer Comments',
    },
  ]);

export const useListingDetails = () =>
  useFieldSection([
    { key: PropertyField.PROPERTY_TYPE, label: 'Property Type' },
    { key: PropertyField.LISTING_ID, label: 'MLS Number' },
    { key: PropertyField.COMPARABLE_YN, label: 'Comparable Y/N' },
    { key: PropertyField.OFFICE_EXCLUSIVE_YN, label: 'Office Exclusive Y/N' },
    { key: PropertyField.MLS_STATUS, label: 'Status' },
    { key: PropertyField.LIST_PRICE, label: 'List Price' },
    { key: PropertyField.LISTING_AGREEMENT, label: 'Listing Agreement Type' },
    { key: PropertyField.LISTING_TERMS, label: 'Listing Terms' },
    { key: PropertyField.EXPIRATION_DATE, label: 'Expiration Date' },
    { key: PropertyField.POSSESSION, label: 'Possession' },
    { key: PropertyField.LISTING_SERVICE, label: 'Listing Service Type' },
  ]);

export const useOwnershipAgents = () =>
  useFieldSection([
    { key: PropertyField.SALE_TYPE, label: 'Sale Type' },
    { key: PropertyField.SHORT_SALE, label: 'Short Sale' },
    { key: PropertyField.DUAL_AGENCY_YN, label: 'Dual Agency Y/N' },
    {
      key: PropertyField.PROSPECTS_EXCLUDED_YN,
      label: 'Prospects Excluded Y/N',
    },
    { key: PropertyField.LIST_AGENT_FULL_NAME, label: 'List Agent' },
    { key: PropertyField.LIST_AGENT_MLS_ID, label: 'List Agent MLS ID' },
    { key: PropertyField.LIST_OFFICE_NAME, label: 'List Office' },
    { key: PropertyField.OWNER_1_NAME, label: 'Owner 1 Name' },
    { key: PropertyField.OWNER_1_EMAIL, label: 'Owner 1 Email' },
    { key: PropertyField.OWNER_1_MOBILE_PHONE, label: 'Owner 1 Mobile' },
    { key: PropertyField.OWNER_2_NAME, label: 'Owner 2 Name' },
    { key: PropertyField.OWNER_2_EMAIL, label: 'Owner 2 Email' },
    { key: PropertyField.OWNER_2_MOBILE_PHONE, label: 'Owner 2 Mobile' },
  ]);

export const useAssociation = () =>
  useFieldSection([
    { key: PropertyField.ASSOCIATION_YN, label: 'HOA Y/N' },
    { key: PropertyField.ASSOCIATION_FEE, label: 'Association Fee' },
    {
      key: PropertyField.ASSOCIATION_FEE_FREQUENCY,
      label: 'Association Fee Frequency',
    },
    {
      key: PropertyField.ASSOCIATION_FEE_INCLUDES,
      label: 'HOA/Condo/Coop Fee Includes',
    },
    { key: PropertyField.SENIOR_COMMUNITY_YN, label: 'Senior Community Y/N' },
  ]);

export const useTaxInfo = () =>
  useFieldSection([
    { key: PropertyField.TAX_ID, label: 'Tax ID Number' },
    {
      key: PropertyField.AUTO_UPDATE_TAX_DATA_YN,
      label: 'Automatically Update Tax Values Y/N',
    },
    { key: PropertyField.ZONING, label: 'Zoning' },
    { key: PropertyField.COUNTY_TAX, label: 'County Tax' },
    { key: PropertyField.TAX_YEAR, label: 'Tax Year' },
    { key: PropertyField.TAX_ANNUAL_AMOUNT, label: 'Tax Annual Amount' },
  ]);

export const usePropertyDetails = () =>
  useFieldSection([
    { key: PropertyField.NEW_CONSTRUCTION_YN, label: 'New Construction Y/N' },
    { key: PropertyField.YEAR_BUILT, label: 'Year Built' },
    { key: PropertyField.ARCHITECTURAL_STYLE, label: 'Style' },
    {
      key: PropertyField.CONSTRUCTION_MATERIALS,
      label: 'Construction Materials',
    },
    { key: PropertyField.LEVELS, label: 'Levels/Stories' },
    { key: PropertyField.WATER_SOURCE, label: 'Water Source' },
    { key: PropertyField.SEWER, label: 'Sewer Septic' },
    { key: PropertyField.HOT_WATER, label: 'Hot Water' },
    { key: PropertyField.COOLING, label: 'Cooling Type' },
    { key: PropertyField.COOLING_FUEL, label: 'Cooling Fuel' },
    { key: PropertyField.HEATING, label: 'Heating Type' },
    { key: PropertyField.HEATING_FUEL, label: 'Heating Fuel' },
    { key: PropertyField.BASEMENT_YN, label: 'Basement Y/N' },
    { key: PropertyField.BASEMENT, label: 'Basement' },
    { key: PropertyField.FOUNDATION_DETAILS, label: 'Foundation Details' },
  ]);

export const useSizeRooms = () =>
  useFieldSection([
    {
      key: PropertyField.ABOVE_GRADE_FINISHED_AREA,
      label: 'Above Grade Finished SQFT',
    },
    { key: PropertyField.LIVING_AREA, label: 'Total Finished SQFT' },
    { key: PropertyField.BEDROOMS_TOTAL, label: 'Bedrooms Total' },
    { key: PropertyField.BATHROOMS_FULL, label: 'Full Bathrooms' },
    { key: PropertyField.BATHROOMS_HALF, label: 'Half Bathrooms' },
  ]);

export const useFeatures = () =>
  useFieldSection([
    {
      key: PropertyField.ACCESSIBILITY_FEATURES,
      label: 'Accessibility Features',
    },
    { key: PropertyField.APPLIANCES, label: 'Appliances' },
    { key: PropertyField.POOL_PRIVATE_YN, label: 'Pool' },
    { key: PropertyField.POOL_FEATURES, label: 'Pool Features' },
    { key: PropertyField.PARKING_FEATURES, label: 'Type Of Parking' },
    { key: PropertyField.PARKING_TOTAL, label: 'Total Parking Spaces' },
    { key: PropertyField.WATER_ORIENTED_YN, label: 'Water Oriented Y/N' },
  ]);

export const useShowingInfo = () =>
  useFieldSection([
    { key: PropertyField.SHOWING, label: 'Showing Requirements' },
    { key: PropertyField.SHOWING_INSTRUCTIONS, label: 'Showing Instructions' },
    { key: PropertyField.LOCK_BOX_TYPE, label: 'Lock Box Type' },
  ]);

// ---------------------------------------------------------------------------
// Public remarks (standalone)
// ---------------------------------------------------------------------------

export function usePreviewRemarks() {
  return useListingField(
    ListingResource.Property,
    PropertyField.PUBLIC_REMARKS,
    '',
  ).value;
}

// ---------------------------------------------------------------------------
// Media
// ---------------------------------------------------------------------------

/** Read media items from the FLC listing store */
export function usePreviewMedia() {
  const media: any[] = useListingStore((state) => state.Media ?? []);

  return media.filter(
    (m: any) => m.MEDIA_CATEGORY === 'Photo' || m.MED_CATEGORY === 'Photo',
  );
}
